/* eslint-disable react-hooks/exhaustive-deps */
// * YOUTUBE SHORTS PLAYER 2.0 //

import React, { useState } from 'react'
import { useWheel, useDrag } from '@use-gesture/react'
import { Lethargy } from 'lethargy'
import YouTubePlayer from 'youtube-player'
import {
  EmptyHeartIcon,
  RedHeartIcon,
  PlayIcon,
  StopVideoIcon,
  VolumeIcon,
  VolumeMuteIcon,
} from 'src/assets/svgs'
import { removeElement } from 'src/utils/preprocessing'
import type { YouTubePlayer as YouTubePlayerType } from 'youtube-player/dist/types'
import useStyles from './style'

interface ShortsPlayerProps {
  videoID: string
  videoList: string[]
  playerState: boolean
}

const lethargy = new Lethargy()

const YoutubeShortsPlayer: React.FC<ShortsPlayerProps> = ({
  videoID,
  videoList,
  playerState,
}: ShortsPlayerProps) => {
  const { classes } = useStyles()
  const playerObj = React.useRef<YouTubePlayerType>()
  const [index, setIndex] = useState(0)
  const [vidData] = React.useState([
    videoID,
    ...removeElement(videoID, videoList),
  ])
  const [videoUrl, setVideoUrl] = React.useState<number | string>('')
  const [width, setWidth] = useState<number>()
  const [playVideoPlayer, setPlayVideoPlayer] = React.useState(false)
  const [muteVideo, setMuteVideo] = React.useState(true)
  // const [touchCoor, setTouchCoor] = React.useState({ startY: 0, endY: 0 })

  React.useLayoutEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth)
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  React.useEffect(() => {
    setVideoUrl(vidData[index])
  }, [index])

  // * destroy player when modal closes
  React.useEffect(() => {
    if (playerState) playerObj.current?.destroy()
    return
  }, [playerState])

  React.useEffect(() => {
    // * cleanup
    playerObj.current?.stopVideo()
    const prevElement = document.getElementById(videoUrl.toString())
    if (prevElement) {
      prevElement.innerHTML = `<video class='videoPlayer' id='video-player-${videoUrl}'></video>`
    }

    const player = YouTubePlayer(`video-player-${videoUrl}`, {
      videoId: videoUrl?.toString(),
      playerVars: {
        autoplay: 1,
        controls: 0,
        modestbranding: 1,
        enablejsapi: 1,
      },
    })
    if (muteVideo) {
      player.mute()
    }
    player.playVideo().then(() => setPlayVideoPlayer(true))
    playerObj.current = player
  }, [videoUrl])

  const clamp = (value: number, min: number, max: number) =>
    Math.max(Math.min(max, value), min)

  const playHandler = () => {
    playerObj.current?.unMute().then(() => setMuteVideo(false))
    if (playVideoPlayer) {
      playerObj.current?.pauseVideo().then(() => {
        setPlayVideoPlayer(false)
      })
      return
    }
    playerObj.current?.playVideo().then(() => {
      setPlayVideoPlayer(true)
    })

    return
  }

  const muteHandler = (event: React.SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()
    if (muteVideo) {
      playerObj.current?.unMute().then(() => setMuteVideo(false))
      return
    }
    playerObj.current?.mute().then(() => setMuteVideo(true))
    return
  }

  const likedVideoHandler = (event: React.SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()
  }

  const bind = useWheel(({ event, last, memo: wait = false }) => {
    if (!last) {
      const s = lethargy.check(event)
      if (s) {
        if (!wait) {
          setIndex((i) => clamp(i - s, 0, vidData.length - 1))
          return true
        }
      } else return false
    } else {
      return false
    }
  })

  const dragBinding = useDrag(({ delta, last }) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, y] = delta

    if (!last) {
      if (y > 5) {
        if (index > 0) {
          setIndex(index - 1)
          return
        }
        return
      }
      if (y < -5) {
        if (index < vidData.length - 1) {
          setIndex(index + 1)
          return
        }
        return
      }
    }
  })

  // /* Touchy Touchy Events */
  // const onTouchStartHandler = (e: React.TouchEvent) =>
  //   setTouchCoor({ startY: e.touches[0].clientY, endY: touchCoor.endY })

  // const onTouchEndHandler = (e: React.TouchEvent) => {
  //   if (e.changedTouches[0].clientY - touchCoor.startY > 50) {
  //     if (index > 0) {
  //       setIndex(index - 1)
  //       return
  //     }
  //     return
  //   }
  //   if (e.changedTouches[0].clientY - touchCoor.startY < -50) {
  //     if (index < vidData.length - 1) {
  //       setIndex(index + 1)
  //       return
  //     }
  //     return
  //   }
  // }
  if (width) {
    if (width < 680)
      return (
        <div className={classes.mobileRoot} {...dragBinding()}>
          <div
            className={classes.slider}
            style={{ transform: `translateY(${-index * 98}vh)` }}
          >
            {vidData.map((i, idx) => {
              return (
                <div key={idx} id={`${i}`}>
                  <video
                    className={classes.videoPlayer}
                    id={`video-player-${videoUrl}`}
                  ></video>
                </div>
              )
            })}
          </div>
          <div
            className={classes.mobilePlayerOverlay}
            // onTouchStart={onTouchStartHandler}
            // onTouchEnd={onTouchEndHandler}
            onClick={playHandler}
          >
            <div className={classes.icons}>
              <div className={classes.icon} onClick={playHandler}>
                {playVideoPlayer ? <StopVideoIcon /> : <PlayIcon />}
              </div>

              <div className={classes.icon} onClick={muteHandler}>
                {muteVideo ? <VolumeMuteIcon /> : <VolumeIcon />}
              </div>
              <div className={classes.icon} onClick={likedVideoHandler}>
                {false ? <RedHeartIcon /> : <EmptyHeartIcon />}
              </div>
            </div>
          </div>
        </div>
      )
  }

  return (
    <div className={classes.root} {...bind()}>
      <div
        className={classes.slider}
        style={{ transform: `translateY(${-index * 98}vh)` }}
      >
        {vidData.map((i, idx) => {
          return (
            <div key={idx} id={`${i}`}>
              <video
                className={classes.videoPlayer}
                id={`video-player-${videoUrl}`}
              ></video>
            </div>
          )
        })}
      </div>
      <div
        className={classes.playerOverlay}
        // onTouchStart={onTouchStartHandler}
        // onTouchEnd={onTouchEndHandler}
        onClick={playHandler}
      >
        <div className={classes.icons}>
          <div className={classes.icon} onClick={playHandler}>
            {playVideoPlayer ? <StopVideoIcon /> : <PlayIcon />}
          </div>

          <div className={classes.icon} onClick={muteHandler}>
            {muteVideo ? <VolumeMuteIcon /> : <VolumeIcon />}
          </div>
          <div className={classes.icon} onClick={likedVideoHandler}>
            {false ? <RedHeartIcon /> : <EmptyHeartIcon />}
          </div>
        </div>
      </div>
    </div>
  )
}

export default YoutubeShortsPlayer
